import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Alert, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { useGetWorkEnvironmentSurveyByIdQuery } from '../../hooks/queries/useGetWorkEnvironmentSurveyByIdQuery';
import { useWorkEnvironmentMeasurementsQuery } from '../../hooks/queries/useWorkEnvironmentMeasurementsQuery';
import { useWorkEnvironmentRiskQuery } from '../../hooks/queries/useWorkEnvironmentRiskQuery';
import { useHmsReportPdf } from '../../hooks/useHmsReportPdf';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { NO_DIMENSIONS, openFromStart, previousMeasurement, questionAreas, QuestionFilter } from './reportView';
import { ReportHero } from './components/ReportHero';
import { SummarySection } from './components/SummarySection';
import { JdrSection } from './components/JdrSection';
import { RiskSection } from './components/RiskSection';
import { QuestionsSection } from './components/QuestionsSection';
import { MethodSection } from './components/MethodSection';
import classes from './WorkEnvironmentReport.module.css';

/** The results of one survey, for the team leader, HR and the safety representative to read or print */
export const WorkEnvironmentReportPage = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const surveyId = Number(useParams<{ surveyId: string }>().surveyId);
  const language = questionLanguageFor(i18n.language);
  const { data: survey } = useGetWorkEnvironmentSurveyByIdQuery(surveyId, { enabled: Boolean(surveyId) });
  const { data, isLoading, isError } = useWorkEnvironmentRiskQuery(surveyId, language);
  const { data: measurements = [] } = useWorkEnvironmentMeasurementsQuery(surveyId);
  const previous = previousMeasurement(measurements, surveyId);
  const { data: previousData } = useWorkEnvironmentRiskQuery(previous?.surveyId ?? 0, language, {
    enabled: Boolean(previous),
  });
  const [filter, setFilter] = useState<QuestionFilter>('all');
  const [open, setOpen] = useState<string[] | null>(null);

  const calculated = data?.calculatedAt ? data : null;
  const risk = calculated?.risk.status === 'assessed' ? calculated.risk : null;
  const dimensions = calculated?.dimensions ?? NO_DIMENSIONS;
  const areas = risk ? questionAreas(risk.areas, t, i18n.language) : [];
  const forPdf = useHmsReportPdf(Boolean(data) && (!previous || Boolean(previousData)));
  // The PDF has every area open, whatever the screen shows
  const openAreas = forPdf ? areas.map((area) => area.key) : (open ?? openFromStart(areas));

  const openArea = (key: string) => {
    setFilter('all');
    setOpen([...new Set([...openAreas, key])]);
    // Wait for the area to open before scrolling to it
    requestAnimationFrame(() => document.getElementById(`area-${key}`)?.scrollIntoView({ behavior: 'smooth' }));
  };

  return (
    <div className={classes.report} data-size="md" data-hms-report>
      <ReportHero
        surveyId={surveyId}
        team={survey?.teamName ?? ''}
        expected={survey?.expectedParticipantsCount ?? 0}
        calculated={calculated}
        measurements={measurements}
      />

      <div className={classes.body}>
        {isLoading && <Skeleton variant="rectangle" width="100%" height={480} />}
        {isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentReport.failed')}</Paragraph>
          </Alert>
        )}
        {data && !calculated && (
          <Alert data-color="info">
            <Paragraph>{t('workEnvironmentReport.unavailable')}</Paragraph>
          </Alert>
        )}
        {calculated?.risk.status === 'insufficientResponses' && (
          <Alert data-color="warning">
            <Paragraph>
              {t('workEnvironmentReport.insufficient', {
                responses: calculated.risk.responses,
                min: calculated.risk.minResponses,
              })}
            </Paragraph>
          </Alert>
        )}
        {risk && calculated && (
          <>
            <SummarySection risk={risk} dimensions={dimensions} expected={survey?.expectedParticipantsCount ?? 0} />
            <JdrSection
              dimensions={dimensions}
              previous={
                previous && previousData?.calculatedAt && previousData.dimensions
                  ? { dimensions: previousData.dimensions, measuredAt: previous.measuredAt }
                  : null
              }
            />
            <RiskSection risk={risk} onOpenArea={openArea} />
            <QuestionsSection
              areas={areas}
              filter={filter}
              onFilterChange={setFilter}
              open={openAreas}
              onOpenChange={setOpen}
            />
            <MethodSection risk={risk} forPdf={forPdf} />
          </>
        )}
      </div>
    </div>
  );
};
